"use client";
import React from "react";
import Image from "next/image";
import Icon from "@/components/icons";
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from "@/components/ui/card";
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { NewProductCardProps } from "./types";
import { getTypeBackgroundColor } from "./utils/mapTypeToColor";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import { mapTypeToProductCardType } from "./utils/mapTypeToProductCardType";
import { renderBadges } from "./utils/renderBadges";
import { ctaBackgroundColors } from "./utils/ctaBackgroundColors";
import { getCompletionTimeText } from "./utils/getCompletionTimeText";
import { getPacingText } from "./utils/getPacingText";
import Tag from "../Tags/Tag";
import Tags from "../Tags";

const fallbackCardHeader: Record<string, string> = {
  light: "/product-cards/default-card-header-light.png",
  dark: "/product-cards/default-card-header-light.png",
};

const EDX_LOGO: string = "/trademark-logos/edx-logo-registered-elm.png";

export function NewProductCardInnerContent({
  name,
  imageUrl,
  type,
  partnerFullName,
  partnerLogoUrl,
  childCoursesCount = 0,
  organizationLogoOverride,
  productCardType,
  showBadges = false,
  showCtaFooter = false,
  cta,
  badges,
  badgesByProduct,
  weeksToComplete,
  pacing,
  level,
  isPartOfProgram,
  popularItemList,
  uuid,
  programType,
  microDegreeTag,
  popularBadgeOverride,
  isNew = false,
  includeToComplete = true,
}: NewProductCardProps) {
  const [imageError, setImageError] = useState(false);
  const cardFallbackImage = fallbackCardHeader["light"];

  const cardHeader = imageUrl?.includes("prod-edx-mktg-edit")
    ? cardFallbackImage
    : imageUrl;

  const partnerImage = organizationLogoOverride
    ? organizationLogoOverride
    : partnerLogoUrl
      ? partnerLogoUrl
      : EDX_LOGO;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [titleHeight, setTitleHeight] = useState(0);

  useLayoutEffect(() => {
    if (titleRef.current) {
      const titleHeight = titleRef.current.getBoundingClientRect().height;
      setTitleHeight(titleHeight);
    }
  }, [name]);

  let partnerArray = Array.isArray(partnerFullName)
    ? partnerFullName
    : [partnerFullName];

  if (titleHeight > 60 && partnerArray.length > 2) {
    partnerArray = partnerArray.slice(0, 1);
    partnerArray.push("...");
  }

  const getTags = () => {
    if (!productCardType) return [];

    const componentList = [
      {
        key: "Level",
        tag: "Level",
        renderer: () => {
          if (!level) return null;
          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon type="level" svgProps={{ width: "20", height: "20" }} />
              <span>{level} level</span>
            </div>
          );
        },
      },
      {
        key: "CompletionTime",
        tag: "CompletionTime",
        renderer: () => {
          const completionTimeText = getCompletionTimeText(
            weeksToComplete,
            includeToComplete,
          );
          if (!completionTimeText) return null;

          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon
                type="time-to-complete"
                svgProps={{ width: "20", height: "20" }}
              />
              <span>{completionTimeText}</span>
            </div>
          );
        },
      },
      {
        key: "Pacing",
        tag: "Pacing",
        renderer: () => {
          const pacingText = getPacingText(pacing);
          if (!pacingText) return null;
          const levelText = level ? `, ${level.toLowerCase()} level` : "";
          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon type="pacing" svgProps={{ width: "20", height: "20" }} />
              <span>
                {pacingText}
                {levelText}
              </span>
            </div>
          );
        },
      },
      {
        key: "IsPartOfProgram",
        tag: "IsPartOfProgram",
        renderer: () => {
          if (!isPartOfProgram) return null;
          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon
                type="part-of-program"
                svgProps={{ width: "20", height: "20" }}
              />
              <span>Part of a Program</span> {/* Need the correct title*/}
            </div>
          );
        },
      },
      {
        key: "CourseCount",
        tag: "CourseCount",
        renderer: () => {
          if (!childCoursesCount || childCoursesCount === 0) return null;
          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon
                type="number-of-courses"
                svgProps={{ width: "20", height: "20" }}
              />
              <span>
                {childCoursesCount} course{childCoursesCount !== 1 ? "s" : ""}
              </span>
            </div>
          );
        },
      },
      {
        key: "LearningType",
        tag: "LearningType",
        renderer: () => {
          if (!type) return null;
          return (
            <div className="flex items-center gap-2 text-sm text-gray-800 mb-2">
              <Icon type="degrees" svgProps={{ width: "20", height: "20" }} />
              <span>{type} Learning Type</span> {/* Need the correct title*/}
            </div>
          );
        },
      },
    ];

    const mappedType = mapTypeToProductCardType(
      type,
    ) as keyof typeof productCardType;

    // Extract tag strings from the array of objects with tags properties
    const tagObjects = productCardType[mappedType] || [];
    const currentTypeTags = Array.isArray(tagObjects)
      ? tagObjects.map((tagObj: any) => tagObj.tags).filter(Boolean)
      : [];

    const orderedComponentList = getSortedComponentsBySectionOrder({
      componentList: componentList.filter((comp) =>
        currentTypeTags.includes(comp.tag),
      ),
      sectionsOrder: currentTypeTags,
      returnObjectList: true,
    });

    const renderedComponents = orderedComponentList
      .map((component) => (
        <Fragment key={component.key}>{component.renderer()}</Fragment>
      ))
      .filter(Boolean);

    return renderedComponents;
  };

  const ctaFooterTextStyle = cta?.showUnderline ? "underline" : "";
  const colorKey = cta?.backgroundColor || "";
  const typeBackgroundColor =
    ctaBackgroundColors[colorKey] ?? getTypeBackgroundColor(type);

  return (
    <Card className="bg-white flex flex-col h-full w-full min-w-[270px] transition-shadow duration-300 ease-in-out hover:shadow-product-card border border-[#CCCCCC] rounded-[12px]">
      <div
        className={`w-full flex items-center justify-between px-4 py-2 rounded-t-xl ${typeBackgroundColor} h-[37px]`}
      >
        <span className="font-bold text-primary-500 text-sm">
          {cta?.topCtaText || type}
        </span>
        {isNew && (
          <div className="my-1">
            <Tag
              title="NEW"
              color="orange"
              className="h-[25px] w-[45px] px-2 py-1 text-xs font-bold [border-radius:4px]"
            />
          </div>
        )}
      </div>
      <CardHeader className="relative rounded-t-xl p-0">
        <Image
          width={302}
          height={88}
          className="m-0 object-cover object-top h-[88px] w-auto"
          src={imageError || !cardHeader ? cardFallbackImage : cardHeader}
          alt={name || "Product Image"}
          aria-hidden="true"
          onError={() => setImageError(true)}
        />
        {partnerImage && (
          <Image
            width={116}
            height={56}
            aria-hidden="true"
            className="bg-white shadow absolute p-2 !mt-0 rounded h-[56px] w-[7.25rem] left-4 object-scale-down object-center"
            style={{ top: "50%", transform: "translateY(-50%)" }}
            src={partnerImage}
            alt={partnerArray[0] || "Partner Logo"}
          />
        )}
      </CardHeader>
      <CardContent className="p-4 pb-0 w-full flex-grow flex flex-col justify-between">
        <div>
          <span
            ref={titleRef}
            className="font-bold text-lg m-0 break-words line-clamp-2 text-gray-800 overflow-hidden text-ellipsis"
          >
            {name}
          </span>
          <div className="flex flex-col w-full">
            {partnerArray?.[0] && (
              <span className="truncate text-gray-800 text-md">
                {partnerArray[0]}
              </span>
            )}
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex flex-col flex-grow gap-1 items-start justify-end px-4 pb-5">
        {showBadges && badges && badges.length > 0 && (
          <div className="flex flex-wrap flex-row gap-2 mt-1">
            <Tags variant="badges" tags={badges} />
          </div>
        )}
        {showBadges && !badges && (
          <div className="flex flex-wrap flex-row gap-2 mt-1">
            {renderBadges({
              monarchBadges: badges,
              type,
              badgesByProduct,
              popularItemList,
              uuid,
              programType,
              microDegreeTag,
              popularBadgeOverride,
            })}
          </div>
        )}
        <div className="mt-4">{getTags()}</div>
      </CardFooter>
      {showCtaFooter && (
        <div
          className={`w-full -mb-px flex items-center justify-center px-4 py-2 rounded-b-xl text-center ${typeBackgroundColor}`}
        >
          <span
            className={`font-bold mr-1 text-primary-500 text-sm ${ctaFooterTextStyle}`}
          >
            {cta?.bottomCtaText || "Learn more"}
          </span>
          {cta?.showIcon && (
            <Icon
              type={cta?.iconName || "add-shopping-cart"}
              svgProps={{ width: "20", height: "20" }}
            />
          )}
        </div>
      )}
    </Card>
  );
}
